// Minimal Brand / Loud Design Studio Portfolio page — monolithic iframe block (p_6)
// Multi-brand: acid / beton / candy / archive

export type MinimalPortfolioBrand = 'acid' | 'beton' | 'candy' | 'archive'
export interface MinimalPortfolioPageData { brand?: MinimalPortfolioBrand }
export const DEFAULT_MINIMAL_PORTFOLIO_DATA: MinimalPortfolioPageData = { brand: 'acid' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const MINIMAL_PORTFOLIO_CSS = `
/* ============ БАЗА / ТЕМЫ ============ */
:root{
  --easing:cubic-bezier(.22,.75,.25,1);
  --disp:'Unbounded',system-ui,sans-serif;
  --body:'IBM Plex Sans',system-ui,sans-serif;
  --mono:'JetBrains Mono',ui-monospace,monospace;
}
body[data-theme="acid"]{
  --bg:#0B0B0B; --ink:#F2FFF2; --paper:#151A12;
  --acc:#C8FF1F; --acc2:#FF3DBE; --acc3:#29F3FF;
  --tshadow:6px 6px 0 var(--acc2), 12px 12px 0 var(--acc3);
}
body[data-theme="beton"]{
  --bg:#DDDBD4; --ink:#141414; --paper:#CFCCC2;
  --acc:#FF2E00; --acc2:#141414; --acc3:#1F43FF;
  --tshadow:8px 8px 0 var(--acc);
}
body[data-theme="candy"]{
  --bg:#FF6FB9; --ink:#2B0B3F; --paper:#FFA8D2;
  --acc:#00E0FF; --acc2:#FFE825; --acc3:#7CFF4F;
  --tshadow:5px 5px 0 var(--ink), 11px 11px 0 var(--acc2);
}
body[data-theme="archive"]{
  --bg:#EFE7D8; --ink:#191310; --paper:#E2D7C3;
  --acc:#B3231B; --acc2:#1D3E8F; --acc3:#C99A2C;
  --tshadow:none;
}
*{margin:0;padding:0;box-sizing:border-box}
html{scroll-behavior:smooth}
body{
  background:var(--bg); color:var(--ink);
  font-family:var(--body); font-size:16px; line-height:1.55;
  overflow-x:hidden; transition:background .5s ease,color .5s ease;
}
::selection{background:var(--acc); color:var(--bg)}
a{color:inherit; text-decoration:none}
img{display:block; max-width:100%}
button{font-family:inherit; color:inherit; background:none; border:none; cursor:pointer}
:focus-visible{outline:3px solid var(--acc); outline-offset:3px}
.display{font-family:var(--disp); text-transform:uppercase}
.mono{font-family:var(--mono)}
.wrap{width:min(1400px,92vw); margin-inline:auto}
.skip{position:absolute; left:-999px; top:0; background:var(--acc); color:var(--bg); padding:10px 16px; z-index:999; font-family:var(--mono)}
.skip:focus{left:12px; top:12px}

/* ============ ШУМ / ПРОГРЕСС / КУРСОР ============ */
.noise{position:fixed; inset:-60%; width:220%; height:220%; z-index:120; pointer-events:none; opacity:.07;
  background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.7'/></svg>");
  animation:grain 9s steps(10) infinite;
}
@keyframes grain{
  0%,100%{transform:translate(0,0)} 10%{transform:translate(-3%,2%)} 20%{transform:translate(2%,-4%)}
  30%{transform:translate(-4%,-2%)} 40%{transform:translate(3%,4%)} 50%{transform:translate(-2%,3%)}
  60%{transform:translate(4%,-3%)} 70%{transform:translate(-3%,-4%)} 80%{transform:translate(2%,2%)} 90%{transform:translate(-4%,3%)}
}
.progress{position:fixed; top:0; left:0; height:4px; width:0; background:var(--acc); z-index:160}
.cursor-dot,.cursor-ring{position:fixed; top:0; left:0; pointer-events:none; z-index:300; display:none}
@media (hover:hover) and (pointer:fine){
  .cursor-dot,.cursor-ring{display:block}
  body{cursor:none} a,button{cursor:none}
}
.cursor-dot{width:8px; height:8px; background:var(--acc)}
.cursor-ring{width:38px; height:38px; border:2px solid var(--ink); mix-blend-mode:difference; transition:width .25s,height .25s,background .25s}
.cursor-ring.big{width:70px; height:70px; background:rgba(255,255,255,.15)}

/* ============ ПРЕЛОАДЕР ============ */
.preloader{position:fixed; inset:0; z-index:400; background:var(--bg); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:14px; transition:transform .7s var(--easing)}
.preloader.done{transform:translateY(-102%)}
.preloader .pl-word{font-family:var(--disp); font-weight:900; font-size:clamp(56px,14vw,160px); text-shadow:var(--tshadow)}
.preloader .pl-num{font-family:var(--mono); font-size:14px; letter-spacing:.2em}
html:not(.js) .preloader{display:none}

/* ============ WIPE-ПЕРЕХОД ============ */
.wipe{position:fixed; inset:0; z-index:250; display:flex; pointer-events:none}
.wipe span{flex:1; background:var(--acc); transform:translateY(102%)}
.wipe span:nth-child(2){background:var(--acc2)} .wipe span:nth-child(3){background:var(--ink)}
.wipe span:nth-child(4){background:var(--acc3)} .wipe span:nth-child(5){background:var(--acc)}
.wipe.run span{animation:wipe 1.05s cubic-bezier(.83,0,.17,1) forwards}
.wipe.run span:nth-child(2){animation-delay:.05s}.wipe.run span:nth-child(3){animation-delay:.1s}
.wipe.run span:nth-child(4){animation-delay:.15s}.wipe.run span:nth-child(5){animation-delay:.2s}
@keyframes wipe{0%{transform:translateY(102%)}45%,55%{transform:translateY(0)}100%{transform:translateY(-102%)}}

/* ============ ШАПКА ============ */
header{position:fixed; top:0; left:0; right:0; z-index:140; background:var(--bg); border-bottom:2px solid var(--ink); transition:background .5s}
.hd{display:flex; align-items:center; justify-content:space-between; gap:16px; padding:12px 0}
.emblem{width:44px; height:44px; flex:none; display:grid; place-items:center; background:var(--acc); border:2px solid var(--ink); box-shadow:3px 3px 0 var(--ink); font-family:var(--disp); font-weight:900; font-size:12px; color:var(--bg); animation:spin 14s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.hd-brand{display:flex; align-items:center; gap:12px; font-family:var(--disp); font-weight:900; font-size:15px; letter-spacing:.02em}
nav.mainnav{display:flex; gap:26px}
nav.mainnav a{font-family:var(--mono); font-size:12px; letter-spacing:.14em; text-transform:uppercase; position:relative; padding:4px 0}
nav.mainnav a::after{content:""; position:absolute; left:0; bottom:0; height:3px; width:0; background:var(--acc); transition:width .3s var(--easing)}
nav.mainnav a:hover::after{width:100%}
.hd-right{display:flex; align-items:center; gap:14px}
.t-squares{display:flex; gap:6px}
.t-sq{width:28px; height:28px; border:2px solid var(--ink); font-family:var(--mono); font-size:11px; font-weight:700; display:grid; place-items:center; background:var(--paper); transition:transform .2s,background .2s}
.t-sq:hover{transform:translateY(-3px) rotate(-4deg)}
.t-sq.on{background:var(--acc); color:var(--bg); transform:rotate(6deg)}
.burger{display:none; width:44px; height:44px; border:2px solid var(--ink); background:var(--acc); position:relative; box-shadow:3px 3px 0 var(--ink)}
.burger i{position:absolute; left:9px; right:9px; height:2px; background:var(--bg); transition:transform .3s,opacity .3s; }
.burger i:nth-child(1){top:13px}.burger i:nth-child(2){top:20px}.burger i:nth-child(3){top:27px}
.burger.open i:nth-child(1){transform:translateY(7px) rotate(45deg)}
.burger.open i:nth-child(2){opacity:0}
.burger.open i:nth-child(3){transform:translateY(-7px) rotate(-45deg)}

/* ============ МОБИЛЬНОЕ МЕНЮ ============ */
.menu{position:fixed; inset:0; z-index:135; background:var(--bg); padding:110px 6vw 40px; transform:translateY(-102%); transition:transform .55s var(--easing); display:flex; flex-direction:column; gap:8px}
.menu.open{transform:none}
.menu a.big{font-family:var(--disp); font-weight:900; text-transform:uppercase; font-size:clamp(30px,9vw,64px); line-height:1.15; border-bottom:2px solid var(--ink); padding:10px 0}
.menu a.big:hover{color:var(--acc); transform:translateX(8px)}
.menu .m-themes{margin-top:auto; display:flex; gap:8px; flex-wrap:wrap}

/* ============ HERO ============ */
.hero{position:relative; padding-top:120px; overflow:hidden}
.hero-bgword{position:absolute; top:-6%; right:-10%; font-family:var(--disp); font-weight:900; font-size:42vw; line-height:1; color:transparent; -webkit-text-stroke:2px var(--ink); opacity:.1; pointer-events:none; transform:rotate(-6deg); animation:drift 16s ease-in-out infinite alternate}
@keyframes drift{from{transform:rotate(-6deg) translateY(0)}to{transform:rotate(-3deg) translateY(40px)}}
.hero-grid{display:grid; grid-template-columns:1.15fr .85fr; gap:clamp(24px,4vw,64px); align-items:center; padding-bottom:clamp(40px,6vw,90px)}
.kicker{font-family:var(--mono); font-size:12px; letter-spacing:.2em; text-transform:uppercase; display:inline-block; border:2px solid var(--ink); background:var(--paper); padding:6px 12px; transform:rotate(-1.5deg); margin-bottom:22px}
.hero-title{font-family:var(--disp); font-weight:900; font-size:clamp(76px,17vw,250px); line-height:.95; letter-spacing:-.01em; text-shadow:var(--tshadow); text-transform:uppercase}
.hero-sub{font-family:var(--disp); font-weight:700; text-transform:uppercase; font-size:clamp(15px,2.2vw,26px); margin-top:26px; max-width:22ch}
.hero-sub b{background:var(--acc); color:var(--bg); padding:0 .3em; display:inline-block; transform:rotate(-1deg)}
.hero-meta{margin-top:26px; font-family:var(--mono); font-size:12px; letter-spacing:.12em; text-transform:uppercase; display:flex; gap:18px; flex-wrap:wrap; opacity:.85}
.hero-cta{margin-top:30px; display:flex; gap:16px; flex-wrap:wrap}
.btn{font-family:var(--mono); font-weight:700; font-size:13px; letter-spacing:.12em; text-transform:uppercase; padding:14px 22px; border:2px solid var(--ink); background:var(--acc); color:var(--bg); box-shadow:4px 4px 0 var(--ink); transition:transform .2s,box-shadow .2s; display:inline-block}
.btn:hover{transform:translate(-2px,-2px); box-shadow:7px 7px 0 var(--ink)}
.btn:active{transform:translate(2px,2px); box-shadow:1px 1px 0 var(--ink)}
.btn.ghost{background:transparent; color:var(--ink)}
.hero-right{display:flex; justify-content:center}
.poster-wrap{position:relative; width:min(100%,460px); transform:rotate(3deg); background:var(--paper); border:3px solid var(--ink); box-shadow:12px 12px 0 var(--acc); padding:12px; transition:transform .4s var(--easing)}
.poster-wrap:hover{transform:rotate(0deg) scale(1.02)}
.poster-wrap img{width:100%; aspect-ratio:1/1; object-fit:cover; border:2px solid var(--ink); filter:saturate(1.05)}
.poster-cap{font-family:var(--mono); font-size:11px; letter-spacing:.14em; text-transform:uppercase; padding-top:10px; display:flex; justify-content:space-between; gap:8px}
.tape{position:absolute; width:96px; height:26px; background:rgba(240,230,190,.75); border:1px dashed rgba(0,0,0,.25); z-index:2}
.tape.t1{top:-14px; left:-30px; transform:rotate(-38deg)}
.tape.t2{bottom:-12px; right:-30px; transform:rotate(-38deg)}
.sticker{position:absolute; font-family:var(--mono); font-weight:700; font-size:12px; letter-spacing:.1em; text-transform:uppercase; border:2px solid var(--ink); background:var(--acc2); color:var(--ink); padding:6px 12px; box-shadow:3px 3px 0 var(--ink); z-index:3}
.sticker.s1{top:-16px; right:8%; transform:rotate(7deg)}
.sticker.s2{bottom:14%; left:-26px; transform:rotate(-9deg); background:var(--acc3)}
.barcode{height:40px; margin-top:10px; background:repeating-linear-gradient(90deg,var(--ink) 0 2px,transparent 2px 5px,var(--ink) 5px 6px,transparent 6px 11px,var(--ink) 11px 14px,transparent 14px 18px)}

/* ============ МАРКИ / MARQUEE ============ */
.marquee{overflow:hidden; white-space:nowrap; border-block:3px solid var(--ink); background:var(--paper); padding:14px 0}
.marquee.acc{background:var(--acc); color:var(--bg)}
.mq-track{display:inline-flex; animation:mq var(--speed,24s) linear infinite; will-change:transform}
.marquee.rev .mq-track{animation-direction:reverse}
.mq-track span{font-family:var(--disp); font-weight:700; text-transform:uppercase; font-size:clamp(16px,2.4vw,28px); padding-inline:1.1rem; display:inline-flex; align-items:center; gap:2.2rem}
.mq-track i{font-style:normal; color:var(--acc)}
.marquee.acc .mq-track i{color:var(--bg)}
@keyframes mq{to{transform:translateX(-50%)}}

/* ============ СЕКЦИИ ============ */
.sec{padding:clamp(64px,9vw,130px) 0; border-top:3px solid var(--ink)}
.sec-head{display:flex; align-items:flex-end; justify-content:space-between; gap:16px; border-bottom:3px solid var(--ink); padding-bottom:18px; margin-bottom:clamp(30px,4vw,56px); flex-wrap:wrap}
.sec-idx{font-family:var(--mono); font-size:13px; letter-spacing:.2em; background:var(--ink); color:var(--bg); padding:4px 10px; display:inline-block; transform:rotate(-2deg)}
.sec-title{font-family:var(--disp); font-weight:900; text-transform:uppercase; font-size:clamp(38px,7vw,104px); line-height:1; margin-top:14px}
.sec-note{font-family:var(--mono); font-size:12px; letter-spacing:.14em; text-transform:uppercase; opacity:.8}

/* reveal */
.rv{opacity:0; transform:translateY(30px) rotate(var(--rvr,0deg)); transition:opacity .7s ease var(--d,0s), transform .7s var(--easing) var(--d,0s)}
.rv.in{opacity:1; transform:none}

/* ============ МАНИФЕСТ ============ */
.manifest-grid{display:grid; grid-template-columns:1.4fr .6fr; gap:clamp(28px,4vw,70px); align-items:start}
.statement{font-family:var(--disp); font-weight:700; text-transform:uppercase; font-size:clamp(20px,3.4vw,46px); line-height:1.25}
.statement .outline{color:transparent; -webkit-text-stroke:1.5px var(--ink)}
.statement .hl{background:var(--acc2); color:var(--ink); padding:0 .25em; display:inline-block; transform:rotate(-1deg)}
.man-side{position:relative; display:flex; flex-direction:column; gap:22px}
.stamp{align-self:flex-start; font-family:var(--mono); font-weight:700; font-size:14px; letter-spacing:.18em; text-transform:uppercase; color:var(--acc); border:3px double var(--acc); padding:10px 18px; transform:rotate(-8deg); text-align:center}
.chips{display:flex; flex-wrap:wrap; gap:10px}
.chip{font-family:var(--mono); font-size:12px; letter-spacing:.1em; text-transform:uppercase; border:2px solid var(--ink); padding:7px 12px; background:var(--paper); box-shadow:3px 3px 0 var(--acc)}
.chip:nth-child(odd){transform:rotate(-2deg)} .chip:nth-child(even){transform:rotate(2deg)}
.stats{display:grid; grid-template-columns:repeat(4,1fr); gap:18px; margin-top:clamp(36px,5vw,70px)}
.stat{border:3px solid var(--ink); background:var(--paper); box-shadow:6px 6px 0 var(--acc); padding:20px 16px; transition:transform .25s var(--easing)}
.stat:hover{transform:translate(-3px,-3px) rotate(-1deg)}
.stat-num{font-family:var(--disp); font-weight:900; font-size:clamp(34px,4.6vw,64px); line-height:1; display:block}
.stat-label{font-family:var(--mono); font-size:11px; letter-spacing:.16em; text-transform:uppercase; opacity:.85}

/* ============ РАБОТЫ ============ */
.works-list{list-style:none; border-bottom:3px solid var(--ink)}
.work{display:grid; grid-template-columns:70px 1fr auto 60px; align-items:center; gap:18px; padding:24px 10px; border-top:3px solid var(--ink); position:relative; transition:background .25s,color .25s}
.work:hover{background:var(--ink); color:var(--bg)}
.w-idx{font-family:var(--mono); font-size:13px; letter-spacing:.1em}
.w-title{font-family:var(--disp); font-weight:900; text-transform:uppercase; font-size:clamp(20px,3.6vw,50px); line-height:1.05}
.w-cat{font-family:var(--mono); font-size:12px; letter-spacing:.12em; text-transform:uppercase; opacity:.75; margin-top:4px}
.w-year{font-family:var(--mono); font-size:13px}
.w-arrow{font-family:var(--disp); font-size:26px; text-align:right; transition:transform .3s var(--easing)}
.work:hover .w-arrow{transform:translateX(10px) rotate(-8deg)}
.w-tag{position:absolute; right:110px; top:50%; transform:translateY(-50%) rotate(-8deg) scale(.5); opacity:0; font-family:var(--mono); font-weight:700; font-size:11px; letter-spacing:.12em; background:var(--acc); color:var(--bg); border:2px solid var(--bg); padding:5px 10px; transition:transform .3s var(--easing),opacity .3s; pointer-events:none}
.work:hover .w-tag{opacity:1; transform:translateY(-50%) rotate(-8deg) scale(1)}
.work:nth-child(odd){transform:rotate(-.3deg)} .work:nth-child(even){transform:rotate(.3deg)}

/* ============ УСЛУГИ ============ */
.srv{border-top:3px solid var(--ink)}
.srv:last-child{border-bottom:3px solid var(--ink)}
.srv-row{display:grid; grid-template-columns:90px 1fr auto 46px; gap:18px; align-items:center; padding:26px 10px; transition:background .25s,color .25s,transform .25s}
.srv-row:hover{background:var(--acc); color:var(--bg); transform:translateX(8px)}
.srv-idx{font-family:var(--mono); font-size:13px}
.srv-name{font-family:var(--disp); font-weight:700; text-transform:uppercase; font-size:clamp(20px,3.4vw,44px)}
.srv-desc{font-family:var(--mono); font-size:12px; letter-spacing:.08em; text-transform:uppercase; opacity:.8; margin-top:6px; max-width:60ch}
.srv-price{font-family:var(--mono); font-weight:700; font-size:14px; letter-spacing:.08em}
.srv-plus{font-family:var(--disp); font-size:26px; text-align:center; transition:transform .35s var(--easing)}
.srv-row:hover .srv-plus{transform:rotate(135deg)}

/* ============ ЦИТАТА ============ */
.quote-sec{padding:clamp(70px,10vw,140px) 0}
.quote{position:relative; max-width:960px; margin-inline:auto; border:3px solid var(--ink); background:var(--paper); box-shadow:14px 14px 0 var(--acc2); padding:clamp(30px,5vw,64px); transform:rotate(-1.2deg)}
.quote::before,.quote::after{content:""; position:absolute; width:110px; height:28px; background:rgba(240,230,190,.75); border:1px dashed rgba(0,0,0,.25)}
.quote::before{top:-16px; left:8%; transform:rotate(-4deg)}
.quote::after{bottom:-16px; right:8%; transform:rotate(4deg)}
.quote p{font-family:var(--disp); font-weight:700; text-transform:uppercase; font-size:clamp(17px,2.8vw,34px); line-height:1.35}
.quote cite{display:block; margin-top:22px; font-family:var(--mono); font-size:12px; letter-spacing:.14em; text-transform:uppercase; font-style:normal; opacity:.8}

/* ============ КОНТАКТ / ФУТЕР ============ */
.contact{padding:clamp(70px,10vw,150px) 0 40px; border-top:3px solid var(--ink); position:relative; overflow:hidden}
.cta-link{display:block; font-family:var(--disp); font-weight:900; text-transform:uppercase; font-size:clamp(40px,9.5vw,150px); line-height:1.02; background-image:linear-gradient(var(--acc),var(--acc)); background-repeat:no-repeat; background-size:0% 88%; background-position:left 88%; transition:background-size .5s var(--easing); width:fit-content}
.cta-link:hover{background-size:100% 88%}
.contact-grid{display:grid; grid-template-columns:1fr 1fr 1fr; gap:26px; margin-top:clamp(36px,5vw,70px); align-items:start}
.c-mail{font-family:var(--mono); font-weight:700; font-size:clamp(15px,2vw,22px); letter-spacing:.04em; border-bottom:3px solid var(--acc); display:inline-block; padding-bottom:4px}
.socials{display:flex; flex-direction:column; gap:8px}
.socials a{font-family:var(--mono); font-size:13px; letter-spacing:.14em; text-transform:uppercase; width:fit-content; position:relative}
.socials a::before{content:"↗ "; color:var(--acc)}
.socials a:hover{background:var(--ink); color:var(--bg)}
.colophon{font-family:var(--mono); font-size:11px; letter-spacing:.1em; text-transform:uppercase; line-height:2; opacity:.85}
.foot-word{font-family:var(--disp); font-weight:900; font-size:clamp(90px,22vw,340px); line-height:.9; text-align:center; color:transparent; -webkit-text-stroke:2px var(--ink); opacity:.25; margin-top:30px; user-select:none}
.foot-bar{border-top:2px solid var(--ink); margin-top:-10px; padding:16px 0; display:flex; justify-content:space-between; gap:14px; flex-wrap:wrap; font-family:var(--mono); font-size:11px; letter-spacing:.12em; text-transform:uppercase}
.foot-bar a:hover{background:var(--acc); color:var(--bg)}

/* ============ FAB / ПИКЕР БРЕНДОВ ============ */
.fab{position:fixed; right:18px; bottom:18px; z-index:145; font-family:var(--mono); font-weight:700; font-size:12px; letter-spacing:.12em; text-transform:uppercase; border:2px solid var(--ink); background:var(--acc2); color:var(--ink); padding:12px 16px; box-shadow:4px 4px 0 var(--ink); transition:transform .2s}
.fab:hover{transform:translate(-2px,-2px) rotate(-2deg)}
.picker{position:fixed; inset:0; z-index:200; background:var(--bg); transform:translateY(103%); transition:transform .6s var(--easing); overflow:auto; padding:90px 4vw 60px}
.picker.open{transform:none}
.picker-head{display:flex; justify-content:space-between; align-items:center; gap:14px; border-bottom:3px solid var(--ink); padding-bottom:16px; margin-bottom:10px; flex-wrap:wrap}
.picker-title{font-family:var(--disp); font-weight:900; text-transform:uppercase; font-size:clamp(24px,4.6vw,56px)}
.picker-close{width:52px; height:52px; border:2px solid var(--ink); background:var(--acc); font-size:22px; box-shadow:4px 4px 0 var(--ink); font-weight:700}
.p-row{display:grid; grid-template-columns:70px 96px 1fr auto 40px; gap:20px; align-items:center; width:100%; text-align:left; border-bottom:3px solid var(--ink); padding:20px 6px; transition:background .25s,color .25s}
.p-row:first-of-type{border-top:3px solid var(--ink)}
.p-row:hover{background:var(--ink); color:var(--bg)}
.p-row.on{background:var(--acc); color:var(--bg)}
.p-num{font-family:var(--mono); font-weight:700; font-size:15px}
.p-thumb{width:96px; height:96px; object-fit:cover; border:2px solid currentColor; transform:rotate(-2deg)}
.p-name{font-family:var(--disp); font-weight:900; text-transform:uppercase; font-size:clamp(20px,3.6vw,44px); line-height:1.05}
.p-desc{font-family:var(--mono); font-size:12px; letter-spacing:.08em; text-transform:uppercase; opacity:.8; margin-top:6px}
.p-sw{display:flex; gap:6px}
.p-sw i{width:20px; height:20px; border:2px solid currentColor; display:block}
.p-arrow{font-family:var(--disp); font-size:22px}
.p-hint{font-family:var(--mono); font-size:11px; letter-spacing:.14em; text-transform:uppercase; opacity:.7; margin-top:18px}

/* ============ АДАПТИВ ============ */
@media (max-width:1020px){
  .hero-grid{grid-template-columns:1fr; }
  .hero-right{order:-1}
  .poster-wrap{width:min(78vw,420px)}
  .manifest-grid{grid-template-columns:1fr}
  .stats{grid-template-columns:repeat(2,1fr)}
  .contact-grid{grid-template-columns:1fr; gap:34px}
  .p-row{grid-template-columns:50px 72px 1fr 34px}
  .p-sw{display:none}
  .p-thumb{width:72px; height:72px}
}
@media (max-width:760px){
  nav.mainnav{display:none}
  .t-squares{display:none}
  .burger{display:block}
  .work{grid-template-columns:40px 1fr 40px; gap:10px}
  .w-year{display:none}
  .w-tag{right:60px}
  .srv-row{grid-template-columns:44px 1fr 34px}
  .srv-price{grid-column:2; grid-row:2; justify-self:start}
  .hero-title{font-size:clamp(64px,20vw,140px)}
  .sticker.s2{left:-8px}
  .foot-bar{justify-content:center; text-align:center}
}
/* ============ REDUCED MOTION ============ */
@media (prefers-reduced-motion: reduce){
  html{scroll-behavior:auto}
  *,*::before,*::after{animation:none !important; transition:none !important}
  .rv{opacity:1; transform:none}
  .cursor-dot,.cursor-ring{display:none !important}
  .mq-track{transform:none}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const MINIMAL_PORTFOLIO_HTML_BODY = `
<a class="skip" href="#main">К содержанию</a>

<div class="preloader" id="preloader" aria-hidden="true">
  <div class="pl-word">BST</div>
  <div class="pl-num" id="plNum">ЗАГРУЗКА ШУМА… 0%</div>
</div>

<div class="noise" aria-hidden="true"></div>
<div class="progress" id="progress" aria-hidden="true"></div>
<div class="cursor-dot" id="cDot" aria-hidden="true"></div>
<div class="cursor-ring" id="cRing" aria-hidden="true"></div>
<div class="wipe" id="wipe" aria-hidden="true"><span></span><span></span><span></span><span></span><span></span></div>

<header>
  <div class="wrap hd">
    <a class="hd-brand" href="#top" data-hover>
      <span class="emblem">BST</span>
      <span id="brandName">BST® ACID CLUB</span>
    </a>
    <nav class="mainnav" aria-label="Основная навигация">
      <a href="#manifest" data-hover>Манифест</a>
      <a href="#works" data-hover>Работы</a>
      <a href="#services" data-hover>Услуги</a>
      <a href="#contact" data-hover>Контакт</a>
    </nav>
    <div class="hd-right">
      <div class="t-squares" role="group" aria-label="Быстрый выбор бренда">
        <button class="t-sq on" data-theme-btn="acid" data-hover>01</button>
        <button class="t-sq" data-theme-btn="beton" data-hover>02</button>
        <button class="t-sq" data-theme-btn="candy" data-hover>03</button>
        <button class="t-sq" data-theme-btn="archive" data-hover>04</button>
      </div>
      <button class="burger" id="burger" aria-label="Меню" data-hover><i></i><i></i><i></i></button>
    </div>
  </div>
</header>

<div class="menu" id="menu">
  <a class="big" href="#manifest" data-hover>Манифест</a>
  <a class="big" href="#works" data-hover>Работы</a>
  <a class="big" href="#services" data-hover>Услуги</a>
  <a class="big" href="#contact" data-hover>Контакт</a>
  <div class="m-themes">
    <button class="btn" data-theme-btn="acid" data-hover>01 ACID</button>
    <button class="btn" data-theme-btn="beton" data-hover>02 БЕТОН</button>
    <button class="btn" data-theme-btn="candy" data-hover>03 САХАР</button>
    <button class="btn" data-theme-btn="archive" data-hover>04 АРХИВ</button>
  </div>
</div>

<main id="main">
<!-- ================= HERO ================= -->
<section class="hero" id="top">
  <div class="hero-bgword" aria-hidden="true">BST</div>
  <div class="wrap hero-grid">
    <div class="hero-left">
      <span class="kicker" data-copy="kicker">СТУДИЯ ГРОМКОГО ДИЗАЙНА — EST. 2019</span>
      <h1 class="hero-title" id="heroTitle">BST</h1>
      <p class="hero-sub" id="heroSub">КРИЧИМ ЦВЕТОМ. <b>ВЕРСТАЕМ</b> НА ВСЮ ГРОМКОСТЬ.</p>
      <div class="hero-meta">
        <span>МСК / ВСЁ МИРОВОЕ</span><span>4 РЕЖИМА БРЕНДА</span><span>ШУМ: ВКЛ</span>
      </div>
      <div class="hero-cta">
        <a class="btn magnet" href="#works" data-hover>Смотреть дела ↓</a>
        <a class="btn ghost magnet" href="#contact" data-hover>Написать нам</a>
      </div>
    </div>
    <div class="hero-right">
      <figure class="poster-wrap" data-hover>
        <span class="tape t1"></span><span class="tape t2"></span>
        <span class="sticker s1" data-copy="sticker1">LIVE 24/7</span>
        <span class="sticker s2" data-copy="sticker2">18+ dB</span>
        <img id="poster" src="" alt="Постер бренда BST в текущем режиме" width="460" height="460">
        <div class="barcode" aria-hidden="true"></div>
        <figcaption class="poster-cap"><span data-copy="caption">POSTER №01 — ACID DROP</span><span>BST®</span></figcaption>
      </figure>
    </div>
  </div>
  <div class="marquee acc" id="mqHero" style="--speed:20s" aria-hidden="true"></div>
</section>

<!-- ================= МАНИФЕСТ ================= -->
<section class="sec" id="manifest">
  <div class="wrap">
    <div class="sec-head rv">
      <div><span class="sec-idx">01</span><h2 class="sec-title">Манифест</h2></div>
      <span class="sec-note">читается вслух / громко</span>
    </div>
    <div class="manifest-grid">
      <p class="statement rv" id="manifestText"></p>
      <aside class="man-side rv" style="--d:.15s">
        <div class="stamp">ОДОБРЕНО<br>ОТДЕЛОМ ШУМА<br>BST®</div>
        <div class="chips" id="chips"></div>
      </aside>
    </div>
    <div class="stats">
      <div class="stat rv"><span class="stat-num" data-count="120">0</span><span class="stat-label" data-t="stat1L">проектов выпущено</span></div>
      <div class="stat rv" style="--d:.1s"><span class="stat-num" data-count="37">0</span><span class="stat-label" data-t="stat2L">наград и шорт-листов</span></div>
      <div class="stat rv" style="--d:.2s"><span class="stat-num" data-count="6">0</span><span class="stat-label" data-t="stat3L">лет громкой работы</span></div>
      <div class="stat rv" style="--d:.3s"><span class="stat-num" data-count="14">0</span><span class="stat-label" data-t="stat4L">человек в банде</span></div>
    </div>
  </div>
</section>

<!-- ================= РАБОТЫ ================= -->
<section class="sec" id="works">
  <div class="wrap">
    <div class="sec-head rv">
      <div><span class="sec-idx">02</span><h2 class="sec-title">Избранные дела</h2></div>
      <span class="sec-note">06 дел / обновлено 2025 / наведи = инверсия</span>
    </div>
    <ol class="works-list rv" id="worksList"></ol>
  </div>
</section>

<div class="marquee rev" style="--speed:30s" aria-hidden="true">
  <div class="mq-track" id="mqAwards"></div>
</div>

<!-- ================= УСЛУГИ ================= -->
<section class="sec" id="services">
  <div class="wrap">
    <div class="sec-head rv">
      <div><span class="sec-idx">03</span><h2 class="sec-title">Услуги</h2></div>
      <span class="sec-note">прайс — по запросу / торг уместен</span>
    </div>
    <div class="srv">
      <div class="srv-row rv" data-hover><span class="srv-idx">/01</span><div><div class="srv-name" data-t="svc1Title">Стратегия</div><div class="srv-desc" data-t="svc1Desc">аудит · позиционирование · нейминг · сначала думаем, потом кричим</div></div><span class="srv-price" data-t="svc1Price">от 300 000 ₽</span><span class="srv-plus">+</span></div>
      <div class="srv-row rv" data-hover><span class="srv-idx">/02</span><div><div class="srv-name" data-t="svc2Title">Айдентика</div><div class="srv-desc" data-t="svc2Desc">логотип · системы · гайдлайны · носители до забора</div></div><span class="srv-price" data-t="svc2Price">от 500 000 ₽</span><span class="srv-plus">+</span></div>
      <div class="srv-row rv" data-hover><span class="srv-idx">/03</span><div><div class="srv-name" data-t="svc3Title">Веб</div><div class="srv-desc" data-t="svc3Desc">сайты · e-com · интерфейсы · адаптив до последнего пикселя</div></div><span class="srv-price" data-t="svc3Price">от 700 000 ₽</span><span class="srv-plus">+</span></div>
      <div class="srv-row rv" data-hover><span class="srv-idx">/04</span><div><div class="srv-name" data-t="svc4Title">Motion</div><div class="srv-desc" data-t="svc4Desc">ролики · титры · шейдеры · 60 fps или ничего</div></div><span class="srv-price" data-t="svc4Price">от 250 000 ₽</span><span class="srv-plus">+</span></div>
      <div class="srv-row rv" data-hover><span class="srv-idx">/05</span><div><div class="srv-name" data-t="svc5Title">Упаковка и носители</div><div class="srv-desc" data-t="svc5Desc">печать · леттерпресс · мерч · стикербомбинг</div></div><span class="srv-price" data-t="svc5Price">от 200 000 ₽</span><span class="srv-plus">+</span></div>
    </div>
  </div>
</section>

<!-- ================= ЦИТАТА ================= -->
<section class="quote-sec">
  <div class="wrap">
    <blockquote class="quote rv" style="--rvr:-1.2deg">
      <p id="quoteText"></p>
      <cite id="quoteAuthor"></cite>
    </blockquote>
  </div>
</section>

<!-- ================= КОНТАКТ ================= -->
<section class="contact" id="contact">
  <div class="wrap">
    <div class="sec-head rv" style="border-bottom:none; padding-bottom:0; margin-bottom:26px">
      <div><span class="sec-idx">04</span><h2 class="sec-title">Контакт</h2></div>
      <span class="sec-note">отвечаем быстрее, чем сохнет плита</span>
    </div>
    <a class="cta-link rv" href="mailto:hello@bst.studio" data-hover id="ctaText">ВКЛЮЧАЙ ГРОМКОСТЬ</a>
    <div class="contact-grid">
      <div class="rv"><a class="c-mail" href="mailto:hello@bst.studio" data-hover>hello@bst.studio</a>
        <p class="mono" style="font-size:12px; margin-top:14px; letter-spacing:.1em; text-transform:uppercase; opacity:.8">бриф — тема письма «ШУМ»</p>
      </div>
      <div class="socials rv" style="--d:.1s">
        <a href="#" data-hover>Telegram</a>
        <a href="#" data-hover>Behance</a>
        <a href="#" data-hover>VK</a>
        <a href="#" data-hover>YouTube</a>
      </div>
      <div class="colophon rv" style="--d:.2s">
        Шрифты: Unbounded + Plex + Mono<br>
        Собрано вручную, без шаблонов<br>
        Система BST-4 // режим: <span id="modeName">ACID</span><br>
        Клавиши 1–4 — смена бренда
      </div>
    </div>
    <div class="foot-word" aria-hidden="true">BST®</div>
    <div class="foot-bar">
      <span>© 2025 BST® STUDIO — СДЕЛАНО ГРОМКО</span>
      <span>МСК: <span id="msk">--:--:--</span></span>
      <a href="#top" data-hover>НАВЕРХ ↑</a>
    </div>
  </div>
</section>
</main>

<button class="fab" id="fab" data-hover>БРЕНД: <span id="fabCode">01 · ACID</span> ⇄</button>

<div class="picker" id="picker" role="dialog" aria-modal="true" aria-label="Выбор режима бренда">
  <div class="picker-head">
    <h2 class="picker-title">Один код — четыре бренда</h2>
    <button class="picker-close" id="pickerClose" aria-label="Закрыть" data-hover>✕</button>
  </div>
  <button class="p-row" data-theme-btn="acid" data-hover>
    <span class="p-num">01</span>
    <img class="p-thumb" src="https://image.qwenlm.ai/public_source/ea0255c8-b9a5-42d7-a357-a5830cae76c0/1d975096a-ca3c-46bb-8723-fbcb139c8a4d.png" alt="">
    <span><span class="p-name">BST Acid Club</span><span class="p-desc">Кислотный рейв: неон, хром, 140 BPM.</span></span>
    <span class="p-sw"><i style="background:#0B0B0B"></i><i style="background:#C8FF1F"></i><i style="background:#FF3DBE"></i><i style="background:#29F3FF"></i></span>
    <span class="p-arrow">→</span>
  </button>
  <button class="p-row" data-theme-btn="beton" data-hover>
    <span class="p-num">02</span>
    <img class="p-thumb" src="https://image.qwenlm.ai/public_source/ea0255c8-b9a5-42d7-a357-a5830cae76c0/1e3c2816a-a802-42b2-8b8d-3a8190ac3b26.png" alt="">
    <span><span class="p-name">BST Бетон</span><span class="p-desc">Брутальный режим: плита, ГОСТ, один красный.</span></span>
    <span class="p-sw"><i style="background:#DDDBD4"></i><i style="background:#141414"></i><i style="background:#FF2E00"></i><i style="background:#1F43FF"></i></span>
    <span class="p-arrow">→</span>
  </button>
  <button class="p-row" data-theme-btn="candy" data-hover>
    <span class="p-num">03</span>
    <img class="p-thumb" src="https://image.qwenlm.ai/public_source/ea0255c8-b9a5-42d7-a357-a5830cae76c0/1423bc20a-f4d4-4917-9daf-b4a19ed22dbf.png" alt="">
    <span><span class="p-name">BST Сахар</span><span class="p-desc">Поп-режим: глянец, стикеры, жвачка.</span></span>
    <span class="p-sw"><i style="background:#FF6FB9"></i><i style="background:#2B0B3F"></i><i style="background:#00E0FF"></i><i style="background:#FFE825"></i></span>
    <span class="p-arrow">→</span>
  </button>
  <button class="p-row" data-theme-btn="archive" data-hover>
    <span class="p-num">04</span>
    <img class="p-thumb" src="https://image.qwenlm.ai/public_source/ea0255c8-b9a5-42d7-a357-a5830cae76c0/16e520eb2-affd-4d42-8628-6928fdd2620f.png" alt="">
    <span><span class="p-name">BST Архив</span><span class="p-desc">Архивный режим: бумага, печати, сургуч.</span></span>
    <span class="p-sw"><i style="background:#EFE7D8"></i><i style="background:#191310"></i><i style="background:#B3231B"></i><i style="background:#1D3E8F"></i></span>
    <span class="p-arrow">→</span>
  </button>
  <p class="p-hint">подсказка: клавиши 1–4 переключают бренд / esc — закрыть</p>
</div>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function minimalPortfolioEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
document.documentElement.classList.add('js');
(function(){
"use strict";
var RM = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
var FINE = window.matchMedia('(hover:hover) and (pointer:fine)').matches;
var $ = function(s){ return document.querySelector(s); };
var $$ = function(s){ return Array.prototype.slice.call(document.querySelectorAll(s)); };

/* ---------- ДАННЫЕ БРЕНДОВ ---------- */
var IMG = {
  acid:"https://image.qwenlm.ai/public_source/ea0255c8-b9a5-42d7-a357-a5830cae76c0/1d975096a-ca3c-46bb-8723-fbcb139c8a4d.png",
  beton:"https://image.qwenlm.ai/public_source/ea0255c8-b9a5-42d7-a357-a5830cae76c0/1e3c2816a-a802-42b2-8b8d-3a8190ac3b26.png",
  candy:"https://image.qwenlm.ai/public_source/ea0255c8-b9a5-42d7-a357-a5830cae76c0/1423bc20a-f4d4-4917-9daf-b4a19ed22dbf.png",
  archive:"https://image.qwenlm.ai/public_source/ea0255c8-b9a5-42d7-a357-a5830cae76c0/16e520eb2-affd-4d42-8628-6928fdd2620f.png"
};
var THEMES = {
  acid:{ code:"01 · ACID", mode:"ACID", brand:"BST® ACID CLUB",
    doc:"BST® ACID CLUB — дизайн-рейв",
    kicker:"СТУДИЯ ГРОМКОГО ДИЗАЙНА — EST. 2019",
    sub:'КРИЧИМ ЦВЕТОМ. <b>ВЕРСТАЕМ</b> НА ВСЮ ГРОМКОСТЬ.',
    caption:"POSTER №01 — ACID DROP", sticker1:"LIVE 24/7", sticker2:"18+ dB",
    marquee:["BST ACID CLUB","ДИЗАЙН КАК РЕЙВ","БЕЗ ТОРМОЗОВ","100% ГРОМКО","NO SLEEP"],
    manifest:'Мы верим, что <span class="outline">тишина — плохой бриф</span>. BST собирает айдентику, сайты и шоу из перегруза: <span class="hl">кислотный цвет</span>, рваный ритм, ноль вежливости.',
    chips:["acid-графика","motion-рейв","клубный веб","стикербомбинг"],
    works:[["NEON МЯСО","фестиваль / айдентика — 2025","2025","OPEN AIR"],["RAVE-АПТЕКА","e-com / упаковка — 2025","2025","SOLD OUT"],["ВОЛНА 303","лейбл / веб — 2024","2024","130 BPM"],["СТРОБОСКОП","приложение / UX — 2024","2024","V2.0"],["КИСЛОТНЫЙ ДОЖДЬ","кампания / арт-дир — 2023","2023","FWA"],["БАС И ТОЧКА","бренд / нейминг — 2023","2023","LOUD"]],
    quote:"«Попросили сделать потише. BST сделали погромче. Теперь очереди до улицы.»",
    quoteAuthor:"— основатель рейва «ВОЛНА 303»", cta:"ВКЛЮЧАЙ ГРОМКОСТЬ"},
  beton:{ code:"02 · БЕТОН", mode:"БЕТОН", brand:"BST® БЕТОН",
    doc:"BST® БЕТОН — тяжёлый дизайн",
    kicker:"ПРОЕКТНОЕ БЮРО ТЯЖЁЛЫХ РЕШЕНИЙ — EST. 2019",
    sub:'ТЯЖЁЛЫЙ ДИЗАЙН ДЛЯ ТЯЖЁЛЫХ ЗАДАЧ. <b>БЕЗ ШТУКАТУРКИ.</b>',
    caption:"ЛИСТ №01 — ОТЛИВКА", sticker1:"ГОСТ 2025", sticker2:"НЕ КРАСИТЬ",
    marquee:["BST БЕТОН","ФОРМА = ФУНКЦИЯ","ЛИТОЙ ДИЗАЙН","БЕЗ ДЕКОРА","ПЛИТА №1"],
    manifest:'Бетон не спорит — он <span class="outline">держит</span>. Мы отливаем бренды и интерфейсы как плиты: <span class="hl">ровно, весомо, на сто лет</span>. Декор отваливается сам.',
    chips:["брутальный веб","пром-айдентика","навигация","типографика-плита"],
    works:[["ЗАВОД №9","промка / ребрендинг — 2025","2025","ЛИТО"],["ПАНЕЛЬКА","девелопмент / сайт — 2025","2025","СДАНО"],["ЦЕХ 3","фуд-холл / айдентика — 2024","2024","АРМАТУРА"],["ОПОРНАЯ СТЕНА","финтех / продукт — 2024","2024","ГОСТ"],["КРАН","логистика / платформа — 2023","2023","10 ТОНН"],["ФУНДАМЕНТ","образование / система — 2023","2023","БЕТОН"]],
    quote:"«Сначала казалось слишком жёстко. Потом конкуренты попросили такой же.»",
    quoteAuthor:"— директор завода «ЦЕХ 3»", cta:"ЗАЛИВАЙ ФУНДАМЕНТ"},
  candy:{ code:"03 · САХАР", mode:"САХАР", brand:"BST® САХАР",
    doc:"BST® САХАР — сладкий поп-дизайн",
    kicker:"КОНДИТЕРСКАЯ ВИЗУАЛЬНЫХ РЕШЕНИЙ — EST. 2019",
    sub:'СЛИШКОМ СЛАДКО, <b>ЧТОБЫ БЫТЬ СКРОМНО.</b>',
    caption:"ЭТИКЕТКА №01 — СВЕЖАЯ ПАРТИЯ", sticker1:"ЖЕВАТЬ 24/7", sticker2:"ВКУС Е606",
    marquee:["BST САХАР","ЖУЙ КРУПНЕЕ","ПОСЫПКА В БРИФЕ","СЛАДКИЙ ДИЗАЙН","ЕЩЁ ОДНУ"],
    manifest:'Мы замешиваем бренды как жвачку: <span class="outline">тянутся, лопаются, запоминаются</span>. Сахарный цвет, детский восторг, <span class="hl">взрослый KPI</span>.',
    chips:["поп-арт айдентика","упаковка-конфета","игровой веб","маскоты"],
    works:[["ЖВАЧКА RECORDS","музыка / упаковка — 2025","2025","БУМ"],["ПУЗЫРЬ","бабл-ти / бренд — 2025","2025","ТРУБОЧКА"],["МАРМЕЛАДНЫЙ ДВОР","снеки / кампания — 2024","2024","ЛИПКО"],["ШУМ-КОЛА","напиток / этикетка — 2024","2024","ШИПИТ"],["КАРАМЕЛЬКА-БАНК","детский финтех — 2023","2023","СЛАДКО"],["ВАТА И ПАР","фестиваль / айдентика — 2023","2023","ТАЕТ"]],
    quote:"«Дизайн такой сладкий, что юристы спросили про акциз.»",
    quoteAuthor:"— CMO сети «ПУЗЫРЬ»", cta:"ДОБАВЬ САХАРА"},
  archive:{ code:"04 · АРХИВ", mode:"АРХИВ", brand:"BST® АРХИВ",
    doc:"BST® АРХИВ — визуальные рукописи",
    kicker:"ОТДЕЛ ВИЗУАЛЬНЫХ РУКОПИСЕЙ — EST. 2019",
    sub:'ШУМ ОФОРМЛЯЕМ <b>ПО ГОСТУ АРХИВА.</b>',
    caption:"ДЕЛО №01 — ПОДШИТО", sticker1:"НЕ СГИБАТЬ", sticker2:"ХРАН. ВЕЧНО",
    marquee:["BST АРХИВ","ДЕЛО №2025","ПОДШИТО","ХРАНИТЬ ВЕЧНО","ПЕЧАТЬ"],
    manifest:'Каждый бренд — дело в архиве: <span class="outline">опись, печати, вещдоки</span>. Мы ведём документацию так, чтобы через сто лет было <span class="hl">шумно</span>.',
    chips:["редакционный дизайн","музейная айдентика","печать","леттерпресс"],
    works:[["МУЗЕЙ ШУМА","культура / айдентика — 2025","2025","ОПИСЬ 1"],["ВЕЧЕРНИЙ БСТ","газета / редизайн — 2025","2025","ТИРАЖ"],["ДЕЛО №13","издательство / серия — 2024","2024","ПРОШИТО"],["ОТТИСК","типография / бренд — 2024","2024","КЛЯСА"],["КАРТОТЕКА","библиотека / веб — 2023","2023","ФОНД 7"],["СУРГУЧ","парфюм / упаковка — 2023","2023","ПЕЧАТЬ"]],
    quote:"«Сдали макеты раньше срока. Подшили раньше, чем мы успели их рассмотреть.»",
    quoteAuthor:"— хранитель «МУЗЕЯ ШУМА»", cta:"ВСЁ ЗАПРОТОКОЛИМ"}
};
var current = "acid";

/* ---------- MARQUEE ---------- */
function buildMarquee(el, items){
  if(!el) return;
  var half = "";
  for(var r=0; r<2; r++){
    for(var i=0;i<items.length;i++){ half += "<span>"+items[i]+" <i>✦</i></span>"; }
  }
  el.innerHTML = half;
}
var AWARDS = ["AWWWARDS — SOTD ×3","FWA OF THE DAY ×2","CSSDA — BEST UI","ШОРТ-ЛИСТ ВСЕГО","ПРЕМИЯ «ШУМ ГОДА»","КЛИЕНТ ВЕРНУЛСЯ ×12"];

/* ---------- SCRAMBLE ---------- */
var GLYPHS = "▚▞#$/\\\\*BST01Ξ";
function scramble(el, finalText){
  if(!el) return;
  if(RM){ el.textContent = finalText; return; }
  var frame = 0, total = 22;
  clearInterval(el._sc);
  el._sc = setInterval(function(){
    var out = "";
    for(var i=0;i<finalText.length;i++){
      var reveal = (frame/total)*finalText.length;
      out += (i < reveal) ? finalText[i] : GLYPHS[Math.floor(Math.random()*GLYPHS.length)];
    }
    el.textContent = out;
    frame++;
    if(frame > total){ clearInterval(el._sc); el.textContent = finalText; }
  }, 34);
}

/* ---------- REVEAL / COUNTERS ---------- */
var io = new IntersectionObserver(function(entries){
  entries.forEach(function(e){
    if(e.isIntersecting){ e.target.classList.add('in'); io.unobserve(e.target); }
  });
},{threshold:.15});
function observe(scope){ (scope||document).querySelectorAll('.rv:not(.in)').forEach(function(n){ io.observe(n); }); }
var cio = new IntersectionObserver(function(entries){
  entries.forEach(function(e){
    if(!e.isIntersecting) return;
    var el = e.target, target = parseInt(el.getAttribute('data-count'),10);
    cio.unobserve(el);
    if(RM){ el.textContent = target; return; }
    var t0 = null;
    function step(ts){ if(!t0) t0 = ts; var p = Math.min((ts-t0)/1200,1);
      el.textContent = Math.round(target * (1-Math.pow(1-p,3)));
      if(p<1) requestAnimationFrame(step); }
    requestAnimationFrame(step);
  });
},{threshold:.5});
$$('.stat-num').forEach(function(n){ cio.observe(n); });

/* ---------- ПРИМЕНЕНИЕ ТЕМЫ ---------- */
function applyTheme(key){
  var t = THEMES[key]; if(!t) return;
  current = key;
  document.body.setAttribute('data-theme', key);
  document.title = t.doc;
  $('#brandName').textContent = t.brand;
  $('#fabCode').textContent = t.code;
  $('#modeName').textContent = t.mode;
  $('#heroSub').innerHTML = t.sub;
  $('[data-copy="kicker"]').textContent = t.kicker;
  $('[data-copy="caption"]').textContent = t.caption;
  $('[data-copy="sticker1"]').textContent = t.sticker1;
  $('[data-copy="sticker2"]').textContent = t.sticker2;
  $('#manifestText').innerHTML = t.manifest;
  $('#quoteText').textContent = t.quote;
  $('#quoteAuthor').textContent = t.quoteAuthor;
  $('#ctaText').textContent = t.cta;
  $('#poster').src = IMG[key];
  $('#chips').innerHTML = t.chips.map(function(c){ return '<span class="chip">'+c+'</span>'; }).join('');
  $('#worksList').innerHTML = t.works.map(function(w,i){
    return '<li class="work" data-hover><span class="w-idx">0'+(i+1)+'</span>'+
      '<div><h3 class="w-title">'+w[0]+'</h3><div class="w-cat">'+w[1]+'</div></div>'+
      '<span class="w-year">'+w[2]+'</span><span class="w-arrow">→</span>'+
      '<span class="w-tag">'+w[3]+'</span></li>';
  }).join('');
  buildMarquee($('#mqHero'), t.marquee);
  $$('.t-sq').forEach(function(b){ b.classList.toggle('on', b.getAttribute('data-theme-btn')===key); });
  $$('.p-row').forEach(function(b){ b.classList.toggle('on', b.getAttribute('data-theme-btn')===key); });
  scramble($('#heroTitle'), "BST");
  observe();
}
function switchTheme(key){
  if(key === current) return;
  closePicker(); closeMenu();
  if(RM){ applyTheme(key); return; }
  var w = $('#wipe');
  w.classList.remove('run'); void w.offsetWidth; w.classList.add('run');
  setTimeout(function(){ applyTheme(key); }, 430);
  setTimeout(function(){ w.classList.remove('run'); }, 1250);
}
$$('[data-theme-btn]').forEach(function(b){
  b.addEventListener('click', function(){ switchTheme(b.getAttribute('data-theme-btn')); });
});

/* ---------- ПИКЕР / МЕНЮ ---------- */
function openPicker(){ $('#picker').classList.add('open'); }
function closePicker(){ $('#picker').classList.remove('open'); }
function openMenu(){ $('#menu').classList.add('open'); $('#burger').classList.add('open'); }
function closeMenu(){ $('#menu').classList.remove('open'); $('#burger').classList.remove('open'); }
$('#fab').addEventListener('click', openPicker);
$('#pickerClose').addEventListener('click', closePicker);
$('#burger').addEventListener('click', function(){
  $('#menu').classList.contains('open') ? closeMenu() : openMenu();
});
$$('#menu a.big').forEach(function(a){ a.addEventListener('click', closeMenu); });
document.addEventListener('keydown', function(e){
  if(e.key === 'Escape'){ closePicker(); closeMenu(); }
  if(['1','2','3','4'].indexOf(e.key) > -1){
    switchTheme(['acid','beton','candy','archive'][parseInt(e.key,10)-1]);
  }
});

/* ---------- ПРОГРЕСС / ЧАСЫ ---------- */
window.addEventListener('scroll', function(){
  var h = document.documentElement;
  var p = h.scrollTop / (h.scrollHeight - h.clientHeight) * 100;
  $('#progress').style.width = p + '%';
}, {passive:true});
function tick(){
  try{ $('#msk').textContent = new Date().toLocaleTimeString('ru-RU',{timeZone:'Europe/Moscow'}); }catch(e){}
}
tick(); setInterval(tick, 1000);

/* ---------- КУРСОР / МАГНИТ ---------- */
if(FINE && !RM){
  var dot = $('#cDot'), ring = $('#cRing');
  var mx=innerWidth/2,my=innerHeight/2,rx=mx,ry=my;
  document.addEventListener('mousemove', function(e){ mx=e.clientX; my=e.clientY;
    dot.style.transform = 'translate('+(mx-4)+'px,'+(my-4)+'px)';
  });
  (function loop(){ rx += (mx-rx)*.14; ry += (my-ry)*.14;
    ring.style.transform = 'translate('+(rx-19)+'px,'+(ry-19)+'px)';
    requestAnimationFrame(loop); })();
  document.addEventListener('mouseover', function(e){
    ring.classList.toggle('big', !!e.target.closest('[data-hover],a,button'));
  });
  $$('.magnet').forEach(function(m){
    m.addEventListener('mousemove', function(e){
      var r = m.getBoundingClientRect();
      var dx = e.clientX - (r.left + r.width/2), dy = e.clientY - (r.top + r.height/2);
      m.style.transform = 'translate('+(dx*.15)+'px,'+(dy*.2)+'px)';
    });
    m.addEventListener('mouseleave', function(){ m.style.transform = ''; });
  });
}

/* ---------- ПРЕЛОАДЕР / СТАРТ ---------- */
buildMarquee($('#mqAwards'), AWARDS);
Object.keys(IMG).forEach(function(k){ var im = new Image(); im.src = IMG[k]; });
applyTheme('${brand}');
if(RM){ $('#preloader').style.display = 'none'; }
else{
  var n = 0, pl = $('#plNum');
  var iv = setInterval(function(){
    n = Math.min(n + 7 + Math.random()*9, 100);
    pl.textContent = 'ЗАГРУЗКА ШУМА… ' + Math.round(n) + '%';
    if(n >= 100){ clearInterval(iv);
      setTimeout(function(){ $('#preloader').classList.add('done');
        setTimeout(function(){ $('#preloader').remove(); }, 800);
      }, 150);
    }
  }, 60);
}
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('.reveal,[data-rv],.rv').forEach(function(el){el.classList.add('in');});
  document.querySelectorAll('[data-count]').forEach(function(el){var t=parseFloat(el.dataset.count),d=+(el.dataset.dec||0);el.textContent=d?t.toFixed(d).replace('.',','):Math.round(t).toLocaleString('ru-RU');});
  document.body.classList.add('loaded','is-ready');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
})();` : ''}
})();
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getMinimalPortfolioPageHtml(data: MinimalPortfolioPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'acid'
  return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>BST Portfolio</title>
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded@5.0.8/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded@5.0.8/900.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-sans@5.0.8/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-sans@5.0.8/600.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jetbrains-mono@5.0.8/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jetbrains-mono@5.0.8/700.css">
<style>${MINIMAL_PORTFOLIO_CSS}</style>
</head>
<body data-theme="${brand}">
${MINIMAL_PORTFOLIO_HTML_BODY}
<script>${minimalPortfolioEngineJs(brand, mode)}<\/script>
</body>
</html>`
}
